import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography, Tooltip } from '@mui/material';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';

interface UpcomingInterview {
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
  interviewStartTime: number;
}

interface UpcomingInterviewsProps {
  interviews: UpcomingInterview[];
}

export const UpcommingInterviews: React.FC<UpcomingInterviewsProps> = ({
  interviews,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  };

  return (
    <Paper variant="outlined" sx={{ mt: 2, borderRadius: 2, p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
        {t(SI.overview.upcomingInterviews)}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          overflowX: 'auto',
          borderTop: 1,
          borderColor: 'divider',
          py: 2,
        }}
      >
        {interviews.length > 0 ? (
          <>
            {interviews.map((interview, index) => {
              const fullName = `${interview.user.firstName} ${interview.user.lastName}`;
              const date = formatDate(interview.interviewStartTime);
              const time = formatTime(interview.interviewStartTime);

              return (
                <Box key={index} sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  {/* Timeline connector (except for first item) */}
                  {index !== 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: -16,
                        top: '50%',
                        width: 16,
                        borderTop: '1px dashed',
                        borderColor: 'divider',
                      }}
                    />
                  )}

                  {/* Interview Card */}
                  <Paper
                    variant="outlined"
                    sx={{
                      width: 176,
                      borderRadius: 2,
                      p: 2,
                      textAlign: 'center',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {date}
                    </Typography>
                    <Box
                      sx={{
                        mx: 'auto',
                        mt: 1,
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        border: 2,
                        borderColor: index === 0 ? 'success.main' : 'grey.400',
                      }}
                    />
                    <Tooltip title={fullName}>
                      <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{
                          mt: 1,
                          fontWeight: 600,
                          color: index === 0 ? 'success.dark' : 'text.primary',
                        }}
                      >
                        {fullName}
                      </Typography>
                    </Tooltip>
                    <Tooltip title={interview.user.email}>
                      <Typography
                        variant="caption"
                        noWrap
                        color="text.secondary"
                        sx={{ display: 'block', fontSize: '11px' }}
                      >
                        {interview.user.email}
                      </Typography>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '11px', mt: 0.5 }}>
                      {time}
                    </Typography>
                  </Paper>
                </Box>
              );
            })}
          </>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              display: 'flex',
              minHeight: 72,
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1.5,
              borderStyle: 'dashed',
              bgcolor: 'action.hover',
              textAlign: 'center',
              p: 2,
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block' }}>
                {t(SI.overview.noUpcoming)}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, fontSize: '11px' }}>
                {t(SI.overview.scheduledWillAppear)}
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>
    </Paper>
  );
};

export default UpcommingInterviews;
